import {
  withAttributeReflection,
  withLifecycle,
  withUpdate,
  applyAsAttributes,
} from '@updater-legacy/components/custom-element';

/**
 * Base class for input presets. An input preset is a custom element that wraps
 * a `upd-input` with some predefined attributes and validators (and possibly
 * other functionality).
 *
 * For example, the `PhoneInput` preset is a `upd-input` with the attributes
 * `type="tel` and `mask="phone"` already applied such that we don't need to
 * repeat this process for all phone inputs.
 *
 * Customizing the wrapped `upd-input` element:
 *   - Attributes defined in `observedAttributes` are forwarded to the wrapped
 *   `upd-input`.
 *   - The wrapped `upd-input` element can be accessed via the `this._input`
 *   reference.
 */
export default class InputPreset extends withUpdate(
  withAttributeReflection(withLifecycle())
) {
  /**
   * @param staticAttributes Any predefined attributes to apply to the wrapped `upd-input`.
   * @param validators Any validators to apply to the wrapped `upd-input`.
   */
  constructor(staticAttributes = {}, validators = []) {
    super();

    this._initInputPreset = () => {
      this.innerHTML = '<upd-input></upd-input>';
      this._input = this.querySelector('upd-input');
      applyAsAttributes(this._input, staticAttributes);
      this._input.validators = validators;
    };
  }

  connected() {
    if (!this._input) {
      this._initInputPreset();
    }

    this.addEventListener('click', this._handleClick);
  }

  disconnected() {
    this.removeEventListener('click', this._handleClick);
  }

  updated(props) {
    const forwardedAttributesMap = Object.keys(props)
      .filter(
        (prop) => this.constructor.observedAttributes.indexOf(prop) !== -1
      )
      .reduce(
        (forwardedAttributes, attr) => ({
          ...forwardedAttributes,
          [attr]: props[attr],
        }),
        {}
      );

    // "Forward" all observed attributes values to the wrapped `upd-input`.
    applyAsAttributes(this._input, forwardedAttributesMap);
  }

  _handleClick = () => {
    // Ensure the wrapped `upd-input` gets focus if the wrapper is clicked. This
    // is necessary in case the wrapper has a mouse hit area that's outside of
    // the wrapped `upd-input`'s boundary (e.g. padding), preventing a click
    // from reaching the wrapped `upd-input`. [ch20788]
    this._input.focusInput();
  };
}
